import React from 'react'
import { AlertTriangle } from 'lucide-react'
import type { AgentActivityStatus } from '../lib/agentUsage'
import { Alert, AlertDescription } from './ui/alert'
import { Badge } from './ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

// ============================================================================
// "No activity in 24h" warning for an agent (Step 6 of
// docs/TOKEN_COST_ACCURACY_PLAN.md: make a dead hook visible). Status comes
// from fetchAgentActivityStatus in src/lib/agentUsage.ts. Renders nothing
// when the agent is active or the status hasn't loaded.
// ============================================================================

function describeLastActivity(status: AgentActivityStatus): string {
  if (!status.lastActivityAt) return 'No check-ins or token usage recorded yet.'
  const d = new Date(status.lastActivityAt)
  if (status.precision === 'day') {
    return `Last activity: ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`
  }
  return `Last activity: ${d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}.`
}

export const AgentStaleBadge: React.FC<{ status: AgentActivityStatus | undefined; className?: string }> = ({
  status,
  className,
}) => {
  if (!status?.isStale) return null
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`inline-flex cursor-help ${className ?? ''}`} onClick={(e) => e.stopPropagation()}>
          <Badge variant="outline" className="gap-1 border-destructive font-medium text-destructive">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            No activity in 24h
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        <p>Hook may not be running. {describeLastActivity(status)}</p>
        <p className="mt-1 text-muted-foreground">
          Check the Claude Code hook is still installed on the owner&apos;s machine.
        </p>
      </TooltipContent>
    </Tooltip>
  )
}

/** Full-sentence variant for a detail page. */
export const AgentStaleNotice: React.FC<{ status: AgentActivityStatus | undefined }> = ({ status }) => {
  if (!status?.isStale) return null
  return (
    // No border radius, per docs/UI_STYLE_GUIDE.md (panels are square).
    <Alert variant="destructive" className="rounded-none">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="text-foreground">
        No activity in 24h — hook may not be running. {describeLastActivity(status)}
      </AlertDescription>
    </Alert>
  )
}
